import type { NormalizedTokenAccount } from "@stepfinance/tokens";
import type { Dispatch, FC, SetStateAction } from "react";
import React, { useContext, useState } from "react";

import {
  useApricotLending,
  useHubbleLending,
  useJet,
  useLarix,
  usePort,
  useRatio,
  useSolend,
  useTulipLending,
} from "../hooks";
import { useHedgeLending } from "../hooks/lending/useHedgeLending";
import { useMarginfiLending } from "../hooks/lending/useMarginFiLending";
import { useSharkyLending } from "../hooks/lending/useSharkyLending";
import { DEFAULT_FRANCIUM_POSITIONS, useFranciumQuery } from "../hooks/queries/lending/useFranciumQuery";
import type { LendingData } from "../types/lending";

type LendingContextState = {
  refreshContext: () => void;
  platforms: LendingData;
  lendingBalance: number;
  setLendingBalance: (balance: number) => void;
  setUserTokenAccountsForTulip: Dispatch<SetStateAction<NormalizedTokenAccount[]>>;
};

const LendingContext = React.createContext<null | LendingContextState>(null);

export const LendingContextProvider: FC<{ children?: React.ReactNode }> = ({ children }) => {
  // FIXME: This is here due to intertwined dependencies where solend
  // marketdata generation logic is buried in logic to get a users positions
  // to be fixed in SF-1531 https://step-finance.atlassian.net/browse/SF-1531
  const [userTokenAccountsForTulip, setUserTokenAccountsForTulip] = useState<NormalizedTokenAccount[]>([]);
  const [lendingBalance, setLendingBalance] = useState(0);
  const solend = useSolend();
  const tulip = useTulipLending(userTokenAccountsForTulip);
  const port = usePort();
  const larix = useLarix();
  const apricot = useApricotLending();
  const jet = useJet();
  const { data: franciumData } = useFranciumQuery();
  const hubble = useHubbleLending();
  const ratio = useRatio();
  const hedge = useHedgeLending();
  const sharky = useSharkyLending();
  const marginfi = useMarginfiLending();

  const refreshContext = () => {};

  return (
    <LendingContext.Provider
      value={{
        refreshContext,
        platforms: {
          solend,
          tulip,
          port,
          larix,
          jet,
          apricot,
          francium: franciumData ?? DEFAULT_FRANCIUM_POSITIONS,
          hubble,
          ratio,
          hedge,
          sharky,
          marginfi,
        },
        lendingBalance,
        setLendingBalance,
        setUserTokenAccountsForTulip,
      }}
    >
      {children}
    </LendingContext.Provider>
  );
};

export function useLendingContext(): LendingContextState {
  const ctx = useContext(LendingContext);
  if (ctx === null) {
    throw new Error("Context not available");
  }
  return ctx;
}
